import type { ElementType } from 'react';
import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import { Avatar, Button, Center, Flex, Icon, Box } from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationLink } from '../../components/NotificationLink';
import { FiEdit } from 'react-icons/fi';
import { NotificationTime } from '../../components/Time';
import { AutoClosedNoteBanner } from '../../components/AutoClosedNoteBanner';
import { KText } from '@/ui-library/components/text';

import {
  ratingOptions,
  getSelectionRatingStyle,
} from '@/constants/scorecard-configs';
import { find } from 'lodash-es';
import type { ReviewCompletedProps } from './ReviewCompleted.interface';

const ReviewCompleted = ({
  notification = {},
  onClose = () => {},
}: ReviewCompletedProps) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes = '', id: notificationId } = notification;
  const data = JSON.parse(decode(custom_attributes));
  const {
    review,
    job,
    contact,
    scorecard_url,
    reviewer = {},
    job_stage,
    scorecard_rating = {},
    auto_closed_note,
  } = data;

  const { name: review_name } = review;
  const { name: reviewer_name } = reviewer;
  const { id: contact_id, name: contact_name, avatar_url } = contact;
  const { url: job_url, name: job_name } = job;
  const { name: job_stage_name } = job_stage;

  const selectedRating = find(ratingOptions, {
    value: scorecard_rating,
  });

  const styleProps = getSelectionRatingStyle(selectedRating);
  const label = selectedRating?.label;
  const IconComponent = selectedRating?.icon;

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="46px" h="46px" borderRadius="full" bg="ats-tertiary">
        <Icon
          // react-icons IconType is wider than Chakra expects
          as={FiEdit as ElementType}
          color="ats-primary"
        />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start">
        {label ? (
          <KText size="sm-regular">
            <KText as="span" size="sm-semi-bold">
              {review_name}
            </KText>
            <KText as="span" ml="1" mr="1">
              feedback submitted:
            </KText>
            <KText as="span" size="sm-semi-bold">
              <NotificationLink
                to={`/contacts/${contact_id}`}
                label={contact_name}
                onClose={onClose}
                notification={notification}
              />
            </KText>
            <KText as="span" ml="1" mr="1">
              -
            </KText>
            <KText as="span" size="sm-semi-bold">
              {job_name}
            </KText>
          </KText>
        ) : (
          <KText>
            <KText as="span" size="sm-semi-bold">
              {reviewer_name}
            </KText>
            <KText as="span" size="sm-regular">
              {' '}
              has submitted a scorecard for candidate{' '}
            </KText>
            <KText as="span" size="sm-semi-bold">
              <NotificationLink
                to={`/contacts/${contact_id}`}
                label={contact_name}
                onClose={onClose}
                notification={notification}
              />
            </KText>
            <KText as="span" size="sm-regular">
              {' '}
              on Job{' '}
            </KText>
            <KText as="span" size="sm-semi-bold">
              <NotificationLink
                to={job_url}
                label={job_name}
                onClose={onClose}
                notification={notification}
              />
            </KText>
          </KText>
        )}

        <KText size="sm-semi-bold">Details</KText>
        <Flex
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          p="14px"
          flexDir="column"
          gap="6px"
          mt="-6px"
        >
          {typeof auto_closed_note === 'string' && auto_closed_note.trim() ? (
            <AutoClosedNoteBanner message={auto_closed_note} />
          ) : null}
          <KText size="sm-semi-bold">Candidate</KText>
          <Flex mb="6px" gap="6px">
            <Avatar
              size="2xs"
              borderRadius="4px"
              className="avatar-xs"
              src={avatar_url}
              name={contact_name}
            />
            <KText size="sm-regular">{contact_name}</KText>
          </Flex>
          <KText size="sm-semi-bold">Job</KText>
          <KText size="sm-regular" mb="6px">
            {job_name}
          </KText>
          <KText size="sm-semi-bold">Stage</KText>
          <KText size="sm-regular" mb="6px">
            {job_stage_name}
          </KText>
          <KText size="sm-semi-bold">Activity</KText>
          <KText size="sm-regular" mb="6px">
            {review_name}
          </KText>
          {selectedRating ? (
            <Box py="2">
              <KText size="sm-semi-bold">Overall evaluation</KText>
              <Flex gap="6px">
                {IconComponent ? (
                  <Icon
                    as={IconComponent}
                    h={styleProps.h || '24px'}
                    w={styleProps.w || '24px'}
                    color={styleProps?.color}
                  />
                ) : null}
                <KText size="sm-regular">{label}</KText>
              </Flex>
            </Box>
          ) : null}
        </Flex>
        <NotificationTime notification={notification} />
        <Button
          w="fit-content"
          variant="primary"
          borderRadius="8px"
          onClick={() => {
            markAsRead({ id: notificationId });
            window.open(scorecard_url, '_self');
            onClose();
          }}
          maxH="34px"
          data-test-id="view-scorecard-button"
        >
          View scorecard
        </Button>
      </Flex>
    </Flex>
  );
};

export const ReviewCompletedNotification = ({
  notification = {},
  onClose = () => {},
}: ReviewCompletedProps) => {
  return (
    <NotificationCard notification={notification}>
      <ReviewCompleted notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
